/**
 * ForgotPasswordPage — Public "forgot my password" entry point.
 *
 * The user enters their registered email; the backend generates a
 * one-time reset token and emails a link to /reset-password?token=…
 *
 * For security (enumeration defense), the backend always returns a
 * generic success message regardless of whether the email exists.
 * We surface that same generic confirmation to the user.
 */
import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import Wordmark from "../components/Wordmark";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/30f41ab2-b4a1-4f65-814e-99ce123f0f30/images/a7d60764e461dd70a1d1703344b38d69ea925fa140e7d762cecedddfa9a8a676.png";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/settings/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
        (lang === "ar" ? "تعذّر إرسال الطلب" : "Failed to submit request")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12"
      data-testid="forgot-password-page"
    >
      <div className="grid lg:grid-cols-2 max-w-5xl w-full glass rounded-3xl overflow-hidden">
        <div className="hidden lg:block relative">
          <img
            src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b3d91]/40 via-[#1a1a1a]/50 to-[#1a1a1a]/90" />
          <div className="relative h-full p-10 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-10 w-auto" aria-hidden />
              <Wordmark size="lg" />
            </div>
            <div>
              <div className="overline mb-3">
                <ShieldCheck className="w-4 h-4 inline-block me-1" />
                {lang === "ar" ? "استعادة الحساب" : "Recover access"}
              </div>
              <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
                {lang === "ar"
                  ? "استرجع حسابك بأمان خلال دقائق."
                  : "Recover your account securely in minutes."}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          <div className="overline mb-2">
            {lang === "ar" ? "نسيت كلمة المرور" : "Forgot password"}
          </div>
          <h1 className="font-display text-3xl tracking-tight font-medium mb-2">
            {lang === "ar" ? "إعادة تعيين كلمة المرور" : "Reset your password"}
          </h1>
          <p className="text-[#6b6b6b] mb-8">
            {lang === "ar"
              ? "أدخل بريدك المسجّل وسنرسل لك رابطاً آمناً للاستعادة."
              : "Enter your registered email and we'll send you a secure link."}
          </p>

          {sent ? (
            <div
              className="rounded-xl border border-[#a8893e]/25 bg-[#fbf7ec] p-5"
              data-testid="forgot-password-sent"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#0f7c3a] mt-0.5" />
                <div className="text-[13px] text-[#3a3a3a] leading-relaxed">
                  {lang === "ar"
                    ? "إذا كان البريد مسجّلاً لدينا، فسنرسل رابط إعادة التعيين خلال دقائق. تحقّق من صندوق الوارد ومجلد الرسائل غير المرغوبة. الرابط صالح لمدة 30 دقيقة."
                    : "If this email is registered, a reset link is on its way. Check your inbox and spam folder. The link is valid for 30 minutes."}
                </div>
              </div>
              <div className="mt-5">
                <Link
                  to="/login"
                  className="btn-ghost text-[13px]"
                  data-testid="back-to-login"
                >
                  <ArrowRight className="w-4 h-4" />
                  {lang === "ar" ? "العودة لتسجيل الدخول" : "Back to sign in"}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
              <div>
                <label className="text-xs text-[#6b6b6b]">
                  {lang === "ar" ? "البريد الإلكتروني" : "Email"}
                </label>
                <div className="relative mt-1">
                  <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b]" />
                  <input
                    className="input-dark ps-10"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn-primary w-full justify-center"
                disabled={busy}
                data-testid="forgot-password-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {busy
                  ? (lang === "ar" ? "جارٍ الإرسال…" : "Sending…")
                  : (lang === "ar" ? "إرسال رابط الاستعادة" : "Send reset link")}
              </button>
            </form>
          )}

          <p className="text-sm text-[#6b6b6b] mt-6">
            {lang === "ar" ? "تذكّرت كلمة المرور؟" : "Remembered your password?"}{" "}
            <Link to="/login" className="text-[#8a6f2e] hover:text-[#c9a55c]" data-testid="switch-to-login">
              {lang === "ar" ? "تسجيل الدخول" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
